import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

// eslint-disable-next-line import/no-extraneous-dependencies
import { datadogRum } from '@datadog/browser-rum';

import { AuthApi, http, UserApi } from '@/api';
import { LocalStorageItems } from '@/constants';
import { useActivityTracking } from '@/hooks/useActivityTracking';
import { IAgency, IUser, IUserRole } from '@/models';
import { setSentryScopes } from '@/services';
import userActivity from '@/services/service.user.activity';
import {
  useAgencyStore,
  useDataStore,
  useToastStore,
  useUserStore,
} from '@/store';
import { formatPersonName } from '@/utils';

// Activity tracking constants
const ACTIVITY_EVENTS = [
  'mousedown',
  'keydown',
  'mousemove',
  'scroll',
  'touchstart',
  'click',
  'focus',
  'input',
];
const REFRESH_INTERVAL = 15 * 60 * 1000; // 15 minutes
const LAST_ACTIVITY_KEY = 'last_activity_timestamp';

const createLoginSessionId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

interface AuthContextTokens {
  accessToken: string | null;
  refreshToken: string | null;
}

interface AuthContextValue extends AuthContextTokens {
  updateToken: (
    newAccessToken: string | null,
    newRefreshToken: string | null,
    onUserDataLoaded?: () => void,
  ) => void;
  logout: (isForceLogout?: boolean) => void;
  isUserDataLoaded: boolean;
  recordActivity: () => void; // New function to record user activity
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext<AuthContextValue>({
  accessToken: null,
  refreshToken: null,
  updateToken: () => {},
  logout: () => {},
  isUserDataLoaded: false,
  recordActivity: () => {}, // Initialize with empty function
});

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [tokens, setTokens] = useState<AuthContextTokens>({
    accessToken: null,
    refreshToken: null,
  });
  const [isUserDataLoaded, setIsUserDataLoaded] = useState(false);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const dataLoadedRef = useRef(false);
  const lastLoadedAgencyIdRef = useRef<string | null>(null);
  const { updateUserStore } = useUserStore();
  const { fetchAgencies } = useAgencyStore();
  const { updateToast } = useToastStore();
  const { loadData } = useDataStore();

  // Record user activity
  const recordActivity = useCallback(() => {
    if (tokens.accessToken) {
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    }
  }, [tokens.accessToken]);

  // Set up activity event listeners
  useEffect(() => {
    if (tokens.accessToken) {
      // Initialize activity timestamp on authentication
      recordActivity();

      // Add event listeners for user activity
      ACTIVITY_EVENTS.forEach((event) => {
        window.addEventListener(event, recordActivity, { passive: true });
      });

      // Clean up on unmount or logout
      return () => {
        ACTIVITY_EVENTS.forEach((event) => {
          window.removeEventListener(event, recordActivity);
        });
      };
    }
  }, [tokens.accessToken, recordActivity]);

  // Token refresh logic
  const refreshTokenIfNeeded = useCallback(async () => {
    if (tokens.accessToken && tokens.refreshToken) {
      try {
        const response = await AuthApi.refreshToken({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
        });

        // Update tokens if refresh was successful
        if (response.data) {
          updateToken(response.data.accessToken, response.data.refreshToken);
        }
      } catch (error: any) {
        // If refresh fails (likely due to inactivity), log the user out
        if (error.response?.status === 401) {
          logout(true);
          updateToast({
            open: true,
            message:
              'Your session has expired due to inactivity. Please log in again.',
          });
        }
      }
    }
  }, [tokens]);

  // Set up periodic token refresh
  useEffect(() => {
    if (tokens.accessToken && tokens.refreshToken) {
      // Clear any existing timer
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }

      // Set up new refresh interval
      refreshTimerRef.current = setInterval(
        refreshTokenIfNeeded,
        REFRESH_INTERVAL,
      );

      // Clean up on unmount or logout
      return () => {
        if (refreshTimerRef.current) {
          clearInterval(refreshTimerRef.current);
          refreshTimerRef.current = null;
        }
      };
    }
  }, [tokens, refreshTokenIfNeeded]);

  useEffect(() => {
    if (tokens.accessToken) {
      fetchUserData();
      fetchAgencies();
    }
  }, [tokens.accessToken]);

  useLayoutEffect(() => {
    let _accessToken = localStorage.getItem(LocalStorageItems.AccessToken);

    // Heartbeat-based logout: if logoutOnClose is enabled and the heartbeat
    // is stale (>10s old or missing), all tabs were closed — clear tokens.
    const logoutOnClose =
      localStorage.getItem(LocalStorageItems.LogoutOnClose) === 'true';
    if (logoutOnClose && _accessToken) {
      const lastHeartbeat = Number(
        localStorage.getItem(LocalStorageItems.LastHeartbeat) || '0',
      );
      const isStale = Date.now() - lastHeartbeat > 10_000;
      if (isStale) {
        localStorage.removeItem(LocalStorageItems.AccessToken);
        localStorage.removeItem(LocalStorageItems.RefreshToken);
        _accessToken = null;
      }
    }
    // Write heartbeat immediately so new tabs opened right after see it
    localStorage.setItem(
      LocalStorageItems.LastHeartbeat,
      Date.now().toString(),
    );

    setTokens({
      accessToken: _accessToken,
      refreshToken: localStorage.getItem(LocalStorageItems.RefreshToken),
    });

    http.setHeader(
      'Authorization',
      _accessToken ? `Bearer ${_accessToken}` : undefined,
    );
  }, []);

  useEffect(() => {
    function storageEventHandler(event: StorageEvent) {
      if (event.key === 'access-token' && !event.newValue) {
        setTokens({
          accessToken: null,
          refreshToken: null,
        });
      }
    }
    window.addEventListener('storage', storageEventHandler);
    return () => {
      window.removeEventListener('storage', storageEventHandler);
    };
  }, []);

  // Heartbeat: update timestamp every 5s so other tabs know we're alive
  useEffect(() => {
    const interval = setInterval(() => {
      localStorage.setItem(
        LocalStorageItems.LastHeartbeat,
        Date.now().toString(),
      );
    }, 5_000);
    return () => clearInterval(interval);
  }, []);

  const getAgencyIdForLoggedInUser = (user: IUser) => {
    if (!user || !user.roles) return null;
    const agencyRole = user.roles.find((role: IUserRole) => role.agency);
    const agency = agencyRole?.agency as IAgency;
    return agency ? agency._id : null;
  };

  const fetchUserData = async (onUserDataLoaded?: () => void) => {
    try {
      http.setHeader('Authorization', `Bearer ${tokens.accessToken}`);

      await Promise.all([UserApi.getCurrentUser(), UserApi.getMyAccount()])
        .then((responses) => {
          const [userRes, accountRes] = responses;
          const userAgencyId = getAgencyIdForLoggedInUser(userRes.data);

          // Update user roles data temporarily
          updateUserStore({
            user: userRes.data,
            account: accountRes.data,
            userAgencyId,
          });

          if (userAgencyId) {
            localStorage.setItem(
              LocalStorageItems.CurrentAgencyId,
              userAgencyId,
            );
          } else {
            localStorage.removeItem(LocalStorageItems.CurrentAgencyId);
          }

          if (
            userAgencyId &&
            (!dataLoadedRef.current ||
              lastLoadedAgencyIdRef.current !== userAgencyId)
          ) {
            loadData();
            dataLoadedRef.current = true;
            lastLoadedAgencyIdRef.current = userAgencyId;
          }

          // Persist logoutOnClose setting so it's available before API calls on next load
          if (accountRes.data?.settings?.auth?.logoutOnClose) {
            localStorage.setItem(LocalStorageItems.LogoutOnClose, 'true');
          } else {
            localStorage.removeItem(LocalStorageItems.LogoutOnClose);
          }

          setSentryScopes(userRes.data, accountRes.data);
          setIsUserDataLoaded(true);
          if (onUserDataLoaded) onUserDataLoaded();

          // After successful login and retrieving user data
          if (userRes.data) {
            // Set user data in Datadog
            datadogRum.setUser({
              id: userRes.data._id,
              name: formatPersonName(userRes.data),
              email: userRes.data.email,
            });
          }

          // Record activity after successful data fetch
          recordActivity();
        })
        .catch((error) => {
          if (error.response?.status === 401) {
            logout(true);
            updateToast({
              open: true,
              message: error.message,
            });
            throw error;
          }
          throw error;
        });
    } catch (error: any) {
      updateToast({ open: true, message: error.message });
    }
  };

  const updateToken = (
    newAccessToken: string | null,
    newRefreshToken: string | null,
    onUserDataLoaded?: () => void,
  ) => {
    if (!tokens.accessToken && newAccessToken) {
      localStorage.setItem(
        LocalStorageItems.SexOffenderDueLoginSessionId,
        createLoginSessionId(),
      );
    }

    localStorage.setItem(LocalStorageItems.AccessToken, newAccessToken ?? '');
    localStorage.setItem(LocalStorageItems.RefreshToken, newRefreshToken ?? '');
    setTokens({
      accessToken: newAccessToken,
      refreshToken: newRefreshToken,
    });
    fetchUserData(onUserDataLoaded);

    // Record activity after token update
    recordActivity();
  };

  const handleInactivity = useCallback(() => {
    logout(true);
    updateToast({
      open: true,
      message:
        'Your session has expired due to inactivity. Please log in again.',
    });
  }, []);

  useActivityTracking(handleInactivity);

  const logout = useCallback(
    async (isForceLogout = false) => {
      try {
        if (tokens.refreshToken && !isForceLogout) {
          await AuthApi.logout({ refreshToken: tokens.refreshToken });
        }
      } catch (error: any) {
        updateToast({ open: true, message: error.message });
      }

      datadogRum.clearUser();
      userActivity.clearActivity();
      localStorage.removeItem(LocalStorageItems.CurrentAgencyId);
      localStorage.removeItem(LocalStorageItems.AccessToken);
      localStorage.removeItem(LocalStorageItems.RefreshToken);
      localStorage.removeItem(LocalStorageItems.SexOffenderDueLoginSessionId);
      localStorage.removeItem(LocalStorageItems.ShowInactiveEmployees);
      localStorage.removeItem(LocalStorageItems.LogoutOnClose);
      localStorage.removeItem(LocalStorageItems.LastHeartbeat);

      updateUserStore({ user: null, account: null });
      http.setHeader('Authorization', undefined);
      dataLoadedRef.current = false;
      lastLoadedAgencyIdRef.current = null;

      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      window.dispatchEvent(
        new StorageEvent('storage', { key: 'access-token', newValue: '' }),
      );
    },
    [tokens.refreshToken, updateToast, updateUserStore],
  );

  return (
    <AuthContext.Provider
      value={{
        ...tokens,
        updateToken,
        logout,
        isUserDataLoaded,
        recordActivity,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
